/* eslint-disable react/prop-types */
import { Circle, GoogleMap, Marker } from "@react-google-maps/api";
import PropTypes from "prop-types";
import React, { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  haversineDistance,
  metersToMiles
} from "../../shared/utilities/distance";
import {
  getCheckoutPartProps,
  isReviewOrderBrandRefresh,
} from "../../pages/ReviewOrder/reviewOrderBrandRefreshClasses";
import locationMarker from "../MapComponent/images/locationMarker.png";
import brandRefreshMapPin from "./imgs/brand-refresh-map-pin.svg";
import brandRefreshZoomPlus from "./imgs/brand-refresh-map-zoom-plus.svg";
import brandRefreshZoomMinus from "./imgs/brand-refresh-map-zoom-minus.svg";
import styles from "./DropoffMap.module.css";

const DarkThemeConstant = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }]
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }]
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#263c3f" }]
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6b9a76" }]
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#38414e" }]
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#212a37" }]
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9ca5b3" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#746855" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2835" }]
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#f3d19c" }]
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#2f3948" }]
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }]
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#17263c" }]
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#515c6d" }]
  },
  {
    featureType: "water",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#17263c" }]
  }
];

const BrandRefreshLightMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#f5f0eb" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b6570" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f0eb" }] },
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [{ color: "#ebe4dc" }]
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#dce8d8" }]
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }]
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#e0d8cf" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#f0e8df" }]
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#d4e4ef" }]
  }
];

const getBrandRefreshMarkerIcon = () => {
  if (typeof window !== "undefined" && window.google?.maps?.Size) {
    return {
      url: brandRefreshMapPin,
      scaledSize: new window.google.maps.Size(60, 60),
      anchor: new window.google.maps.Point(30, 54)
    };
  }
  return brandRefreshMapPin;
};

const DropoffMap = ({
  origin,
  value,
  radiusMeters,
  onChange,
  theme: themeProp
}) => {
  const theme = useSelector(state => state.theme) ?? themeProp;
  const brandRefresh = isReviewOrderBrandRefresh();
  const [markerPosition, setMarkerPosition] = useState(value || origin);
  const mapRef = useRef(null);

  const constrainToRadius = useCallback(
    position => {
      const distance = haversineDistance(origin, position);
      const isValid = distance <= radiusMeters;

      // If the marker is dragged outside the radius, constrain it to the circle edge
      if (!isValid) {
        // Calculate the bearing from origin to the dragged position
        const bearing = Math.atan2(
          position.lng - origin.lng,
          position.lat - origin.lat
        );

        // Convert radiusMeters to approximate degrees
        // 1 degree of latitude ≈ 111,320 meters
        const latOffset = (radiusMeters / 111320) * Math.cos(bearing);
        const lngOffset = (radiusMeters / (111320 * Math.cos(origin.lat * Math.PI / 180))) * Math.sin(bearing);

        // Return the position constrained to the edge of the circle
        return {
          lat: origin.lat + latOffset,
          lng: origin.lng + lngOffset
        };
      }

      return position;
    },
    [origin, radiusMeters]
  );

  const handleMarkerDrag = useCallback(
    e => {
      const newPosition = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng()
      };

      const constrainedPosition = constrainToRadius(newPosition);
      setMarkerPosition(constrainedPosition);
    },
    [constrainToRadius]
  );

  const handleMarkerDragEnd = useCallback(
    e => {
      const newPosition = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng()
      };

      const constrainedPosition = constrainToRadius(newPosition);
      setMarkerPosition(constrainedPosition);

      const finalDistance = haversineDistance(origin, constrainedPosition);
      const distanceMiles = metersToMiles(finalDistance);

      if (onChange) {
        onChange({
          coords: constrainedPosition,
          distance: distanceMiles,
          isValid: finalDistance <= radiusMeters
        });
      }
    },
    [origin, radiusMeters, onChange, constrainToRadius]
  );

  const handleMapKeyDown = useCallback(event => {
    if (event.key === "Tab" || event.key === "Enter" || event.key === " ") {
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
      }
    }
  }, []);

  const handleMapLoad = useCallback(map => {
    mapRef.current = map;
  }, []);

  const handleZoomIn = useCallback(() => {
    if (!mapRef.current) {
      return;
    }
    const currentZoom = mapRef.current.getZoom() || 17;
    mapRef.current.setZoom(currentZoom + 1);
  }, []);

  const handleZoomOut = useCallback(() => {
    if (!mapRef.current) {
      return;
    }
    const currentZoom = mapRef.current.getZoom() || 17;
    mapRef.current.setZoom(Math.max(1, currentZoom - 1));
  }, []);

  if (!origin?.lat || !origin?.lng) {
    return null;
  }

  const mapStyles = (() => {
    if (brandRefresh) {
      return BrandRefreshLightMapStyles;
    }
    return theme?.theme === "dark" ? DarkThemeConstant : null;
  })();

  const circleColor = brandRefresh ? "#3B0055" : "#5e2975";
  const markerIcon = brandRefresh ? getBrandRefreshMarkerIcon() : locationMarker;

  return (
    <div
      id="dropoff-map"
      className={brandRefresh ? styles.brandMapShell : null}
      data-part={getCheckoutPartProps("dropoff-map")}
    >
      <GoogleMap
        options={{
          fullscreenControl: false,
          mapTypeControl: false,
          scrollwheel: true,
          streetViewControl: false,
          zoomControl: !brandRefresh,
          styles: mapStyles,
          keyboardShortcuts: true,
          gestureHandling: "cooperative"
        }}
        mapContainerClassName={
          brandRefresh
            ? `${styles.mapContainer} ${styles.brandMapContainer}`
            : styles.mapContainer
        }
        zoom={17}
        center={origin}
        onLoad={handleMapLoad}
        onKeyDown={handleMapKeyDown}
        role="application"
        aria-label="Dropoff location map - drag the pin to adjust delivery location"
        tabIndex={0}
      >
        {/* Circle showing the allowed radius */}
        <Circle
          center={origin}
          radius={radiusMeters}
          options={{
            fillColor: circleColor,
            fillOpacity: brandRefresh ? 0.08 : 0.15,
            strokeColor: circleColor,
            strokeOpacity: brandRefresh ? 0.35 : 0.8,
            strokeWeight: brandRefresh ? 1.5 : 2,
            clickable: false,
            draggable: false,
            editable: false,
            geodesic: false,
            zIndex: 1
          }}
        />

        {/* Draggable marker */}
        <Marker
          icon={markerIcon}
          position={markerPosition}
          draggable
          onDrag={handleMarkerDrag}
          onDragEnd={handleMarkerDragEnd}
          title="Dropoff location (drag to adjust)"
          options={{
            title: "Dropoff location (drag to adjust)",
            optimized: false
          }}
        />
      </GoogleMap>

      {brandRefresh ? (
        <div
          className={styles.brandZoomControls}
          data-part={getCheckoutPartProps("dropoff-map-zoom")}
        >
          <button
            type="button"
            className={styles.brandZoomButton}
            onClick={handleZoomIn}
            aria-label="Zoom in"
            data-part={getCheckoutPartProps("dropoff-map-zoom-in")}
          >
            <img src={brandRefreshZoomPlus} alt="" width={24} height={24} />
          </button>
          <div className={styles.brandZoomDivider} aria-hidden="true" />
          <button
            type="button"
            className={styles.brandZoomButton}
            onClick={handleZoomOut}
            aria-label="Zoom out"
            data-part={getCheckoutPartProps("dropoff-map-zoom-out")}
          >
            <img src={brandRefreshZoomMinus} alt="" width={24} height={24} />
          </button>
        </div>
      ) : null}
    </div>
  );
};

DropoffMap.propTypes = {
  origin: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired
  }).isRequired,
  value: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  }),
  radiusMeters: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  theme: PropTypes.shape({
    theme: PropTypes.string
  })
};

DropoffMap.defaultProps = {
  value: null,
  theme: null
};

export default DropoffMap;
